import { type ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import { COLORS } from 'theme/MainTheme';
import { type Theme, Box, Drawer, Container } from '@mui/material';
import { useAdminModeContext } from 'contexts';
import {StateSetter} from 'types';
import {DESKTOP_MENU_WIDTH} from 'constants/offsets';

const MOBILE_MENU_WIDTH = '100%';

const useStyles = makeStyles((theme: Theme) => ({
  menuBg: {
    backgroundColor: COLORS.WHITE,
    zIndex: 1,
    height: '100vh',
    overflow: 'auto',

    [theme.breakpoints.down('md')]: {
      overflow: 'auto',
      position: 'fixed',
      width: MOBILE_MENU_WIDTH,
    },

    [theme.breakpoints.up('md')]: {
      width: DESKTOP_MENU_WIDTH,
    },
  },
  adminMenu: {
    boxShadow: '0px 3px 5px 1px rgb(0 0 0 / 25%)',
    height: '100%',
  },
  mainMenu: {
    background:
      'linear-gradient(-185deg, #FFFFFF, 75%, rgba(75, 85, 222, 0.3))',
    height: '100%',
  },
}), { name: 'AppMenuBackground' });

type AppMenuBackgroundProps = {
  children: ReactNode;
  mobile?: boolean;
  isOpened?: boolean;
  setIsOpened?: StateSetter<boolean>;
  'data-testid'?: string;
};

const AppMenuBackground = ({ 'data-testid': dataTestId, children, mobile, isOpened, setIsOpened }: AppMenuBackgroundProps): JSX.Element => {
  const classes = useStyles();
  const { isAdminMode } = useAdminModeContext();
  const menuClassName = isAdminMode ? classes.adminMenu : classes.mainMenu;

  const handleDrawerToggle = (): void => setIsOpened(isOpened => !isOpened);

  const drawerWidth = mobile ? MOBILE_MENU_WIDTH : DESKTOP_MENU_WIDTH;
  
  return (
    <Box
      component='nav'
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label='Menu'
    >
      <Drawer
        data-testid={dataTestId}
        classes={{paper: menuClassName}}
        variant={mobile ? 'temporary' : 'permanent'}
        open={isOpened}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          background: 'white'
        }}
      >
        <Container>
          {children}
        </Container>
      </Drawer>
    </Box>
  );
};

export default AppMenuBackground;
